import { useState } from 'react'

import { ru, spinRate } from '../../lib/physics'
import { Predict, Slider, useFrames } from './common'

/**
 * Балерина и Земля (урок 3): фигуристка прижимает руки — и вращается быстрее. Так же Земля:
 * осенью листва опадает ближе к оси, и сутки чуть короче. Поэтому вращение Земли — плохой
 * эталон секунды, и сегодня секунду задают атомные часы.
 */

const ω0 = 3 // рад/с при разведённых руках
const R0 = 0.8 // м

export default function SpinLab() {
  const [r, setR] = useState(R0)
  const [угол, setУгол] = useState(0)
  const [раньше, setРаньше] = useState(R0)

  const ω = spinRate(ω0, R0, r)
  useFrames((dt) => setУгол((a) => (a + ω * dt) % (2 * Math.PI)))

  const длина = 30 + r * 110
  const рука = (знак: number) => {
    const x = 180 + знак * длина * Math.cos(угол)
    const y = 120 + знак * длина * Math.sin(угол) * 0.35
    return (
      <g>
        <line x1={180} y1={120} x2={x} y2={y} className="ink" strokeWidth={6} strokeLinecap="round" />
        <circle cx={x} cy={y} r={7} className="fill-body ink" strokeWidth={1.5} />
      </g>
    )
  }

  return (
    <div className="card">
      <svg className="lab-svg" viewBox="0 0 360 230" role="img" aria-label="Вращающаяся фигуристка">
        <ellipse cx={180} cy={190} rx={150} ry={22} className="fill-ice" />
        <line x1={180} y1={40} x2={180} y2={190} className="ink-muted" strokeDasharray="4 4" />
        {Math.sin(угол) < 0 && рука(1)}
        {Math.sin(угол) >= 0 && рука(-1)}
        <rect x={168} y={80} width={24} height={100} rx={10} className="fill-skater ink" strokeWidth={1.5} />
        <circle cx={180} cy={68} r={13} className="fill-skin ink" strokeWidth={1.5} />
        {Math.sin(угол) >= 0 && рука(1)}
        {Math.sin(угол) < 0 && рука(-1)}
      </svg>

      <div className="lab-big">
        Вращение: <b>{ru(ω / (2 * Math.PI), 2)}</b> оборота в секунду
      </div>
      <div className="lab-readout lab-readout-col">
        <span>руки на расстоянии {ru(r, 1)} м от оси</span>
        <span>{r < R0 - 0.05 ? `в ${ru(ω / ω0, 1)} раза быстрее, чем с разведёнными руками` : 'руки разведены'}</span>
      </div>

      <Predict
        question="Что будет, если фигуристка прижмёт руки к телу?"
        options={['закружится быстрее', 'замедлится', 'ничего не изменится']}
        right={0}
        revealed={r < раньше - 0.3}
        explain="Чем ближе масса к оси вращения, тем быстрее вращение. Земля так же: осенью листва опадает ближе к оси — Земля крутится чуть быстрее, весной — медленнее. Поэтому сутки — неточный эталон времени, и секунду теперь задают атомные часы."
      />

      <Slider label="Руки от оси" value={r} min={0.2} max={0.8} step={0.05} unit="м" shown={ru(r, 2)} onChange={(v) => { if (v > r) setРаньше(v); setR(v) }} />
      <p className="muted small">
        Атомные часы так точны, что за всё время существования Вселенной ошиблись бы лишь на
        несколько секунд; маленькие атомные часы летают на каждом спутнике навигации.
      </p>
    </div>
  )
}
